import { Box, Flex, Heading, Icon, SimpleGrid, Text, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { useTranslation } from "react-i18next";
import {
  FiArrowUpRight,
  FiCheckCircle,
  FiCompass,
  FiEdit3,
  FiMessageSquare,
  FiSearch,
  FiZap,
} from "react-icons/fi";

interface Props {
  onPrompt: (text: string) => void;
}

interface Capability {
  icon: IconType;
  title: string;
  description: string;
}

export const AssistantEmptyState = ({ onPrompt }: Props) => {
  const { t } = useTranslation();

  const capabilities: Capability[] = [
    {
      icon: FiCompass,
      title: t("Overview and status"),
      description: t("Get an overview of your procurements, deadlines and status."),
    },
    {
      icon: FiArrowUpRight,
      title: t("Find and open"),
      description: t("Find and open a specific procurement or contract."),
    },
    {
      icon: FiMessageSquare,
      title: t("Supplier dialog and decisions"),
      description: t("See supplier questions, evaluation, award letters and announcements."),
    },
    {
      icon: FiSearch,
      title: t("Search the documents"),
      description: t("Search the tender documents for what you need."),
    },
    {
      icon: FiEdit3,
      title: t("Draft texts"),
      description: t("Draft a supplier reply, an award or rejection letter, or a description."),
    },
    {
      icon: FiCheckCircle,
      title: t("Act with confirmation"),
      description: t("Carry out actions with confirmation — for example answer a question or award."),
    },
  ];

  const examplePrompts: string[] = [
    t("Show my open procurements"),
    t("How many bids are on the most recent procurement?"),
    t("What does the tender document say about ISO 9001?"),
    t("Draft a reply to a supplier question"),
  ];

  return (
    <Flex direction="column" align="center" textAlign="center" py="8" px="4">
      <Flex
        w="64px"
        h="64px"
        rounded="2xl"
        align="center"
        justify="center"
        bg="smPrimarySofter"
        position="relative"
        mb="5"
      >
        <Icon as={FiMessageSquare} color="smPrimary" boxSize="7" />
        <Flex
          position="absolute"
          bottom="-1"
          right="-1"
          w="26px"
          h="26px"
          rounded="full"
          align="center"
          justify="center"
          bg="smPrimarySolid"
          borderWidth="2px"
          borderColor="smBackground"
        >
          <Icon as={FiZap} color="white" boxSize="3.5" />
        </Flex>
      </Flex>

      <Heading size="md" color="smText" mb="2">
        {t("How can I help with your procurements?")}
      </Heading>
      <Text fontSize="sm" color="smMuted" maxW="lg" mb="8">
        {t(
          "I work across your procurements, contracts and tender documents — ask a question, search, draft a text, or let me take you straight to the right page.",
        )}
      </Text>

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="3" w="full" mb="8">
        {capabilities.map((capability) => (
          <Flex
            key={capability.title}
            direction="column"
            textAlign="left"
            p="4"
            rounded="2xl"
            borderWidth="1px"
            borderColor="smBorder"
            bg="smBackground"
          >
            <Flex w="34px" h="34px" rounded="lg" align="center" justify="center" mb="3" bg="smPrimarySofter">
              <Icon as={capability.icon} color="smPrimary" boxSize="4" />
            </Flex>
            <Text fontWeight="bold" color="smText" mb="1">
              {capability.title}
            </Text>
            <Text fontSize="sm" color="smMuted">
              {capability.description}
            </Text>
          </Flex>
        ))}
      </SimpleGrid>

      <VStack align="stretch" spacing="3" w="full">
        <Text fontSize="xs" fontWeight="bold" color="smTextDisabled" textTransform="uppercase" letterSpacing="wide">
          {t("Try asking")}
        </Text>
        <Wrap spacing="2" justify="center">
          {examplePrompts.map((prompt) => (
            <WrapItem key={prompt}>
              <Box
                as="button"
                type="button"
                onClick={() => onPrompt(prompt)}
                textAlign="left"
                px="4"
                py="2"
                rounded="full"
                borderWidth="1px"
                borderColor="smBorder"
                bg="smBackground"
                fontSize="sm"
                color="smText"
                _hover={{ borderColor: "smPrimary", bg: "smPrimarySofter" }}
              >
                {prompt}
              </Box>
            </WrapItem>
          ))}
        </Wrap>
      </VStack>
    </Flex>
  );
};
